"use client";

import { usePathname, useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { Languages, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { locales, localeNames, type Locale } from "@/i18n/config";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ThemeToggle";

export function Navbar() {
	const pathname = usePathname();
	const router = useRouter();
	const locale = useLocale();

	const handleLocaleChange = (newLocale: Locale) => {
		const currentPath = pathname.replace(`/${locale}`, "");
		router.push(`/${newLocale}${currentPath || ""}`);
	};

	return (
		<header aria-label="Site header" className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
			<SidebarTrigger className="-ms-1" />
			<Separator orientation="vertical" className="me-2 h-4!" />
			<div className="flex-1" />
			<ThemeToggle />
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button variant="outline" size="icon" aria-label={`Switch language (${localeNames[locale as Locale]})`}>
						<Languages aria-hidden="true" className="size-5" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" role="menu" aria-label="Select language">
					{locales.map((loc) => (
						<DropdownMenuItem
							key={loc}
							role="menuitemradio"
							aria-checked={loc === locale}
							onClick={() => handleLocaleChange(loc)}
						>
							<Check aria-hidden="true" className={cn("me-2 size-4", loc !== locale && "invisible")} />
							{localeNames[loc]}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		</header>
	);
}
